import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Grid,
  Typography,
  Card,
  Switch,
  Button as MuiButton,
  Modal,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import Button from "components/CustomButtons/Button.js";
import { colors } from '../components/Theme/WebTheme';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TodayIcon from '@mui/icons-material/Today';
import AlertDialog from '../components/AlertDialog';
import { useTranslation } from "react-i18next";
import RolesAPI from '../common/rolesApi';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '20px'
  },
  headerSection: {
    marginBottom: '30px'
  },
  statsCard: {
    padding: '20px',
    textAlign: 'center',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  roleCard: {
    margin: '10px 0',
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[8]
    }
  },
  roleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px 8px 20px'
  },
  roleTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#333'
  },
  roleDescription: {
    color: '#666',
    fontSize: '0.9rem',
    margin: '8px 0'
  },
  permissionsSection: {
    padding: '0 20px 16px 20px'
  },
  permissionChip: {
    margin: '2px',
    fontSize: '0.75rem'
  },
  actionsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 20px'
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  permissionsGrid: {
    marginTop: '16px'
  },
  createButton: {
    marginBottom: '20px'
  },
  userCountChip: {
    backgroundColor: colors.MAIN_COLOR,
    color: 'white'
  },
  activeChip: {
    backgroundColor: '#4CAF50',
    color: 'white'
  },
  inactiveChip: {
    backgroundColor: '#F44336',
    color: 'white'
  },
  tableContainer: {
    marginTop: '20px'
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f9f9f9'
    }
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  permissionsCell: {
    maxWidth: '200px'
  }
}));

export default function RolesTab() {
  const classes = useStyles();
  const { t } = useTranslation();

  const [roles, setRoles] = useState([]);
  const [availablePages, setAvailablePages] = useState({});
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    isActive: true
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading roles...');
      const response = await RolesAPI.getRoles();

      console.log('📊 Roles API Response:', response);

      if (response.success) {
        setRoles(response.roles || []);
        setAvailablePages(response.availablePages || {});
        console.log('✅ Roles loaded:', response.roles?.length || 0);
        console.log('📋 Available pages:', Object.keys(response.availablePages || {}));
      } else {
        console.error('❌ Failed to load roles:', response.error);
        showSnackbar(response.error || t('error_loading_roles'), 'error');
      }
    } catch (error) {
      console.error('❌ Error loading roles:', error);
      showSnackbar(t('error_loading_roles'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showSnackbar]);

  useEffect(() => {
    loadRoles();
    loadStatistics();
  }, [loadRoles]);



  const loadStatistics = async () => {
    try {
      const response = await RolesAPI.getRoleStatistics();
      if (response.success) {
        setStatistics(response.statistics || {});
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };


  const handleOpenModal = (role = null) => {
    console.log('🔧 Opening modal for role:', role);
    console.log('📋 Available pages count:', Object.keys(availablePages).length);
    console.log('📋 Available pages:', availablePages);

    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || [],
        isActive: role.isActive !== undefined ? role.isActive : true
      });
      console.log('✏️ Editing role:', role.name, 'with permissions:', role.permissions);
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        permissions: [],
        isActive: true
      });
      console.log('➕ Creating new role');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRole(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  const handleSaveRole = async () => {
    try {
      // Validate form
      const validation = RolesAPI.validateRoleData(formData);
      if (!validation.isValid) {
        showSnackbar(validation.errors.join(', '), 'error');
        return;
      }

      let response;
      if (editingRole) {
        response = await RolesAPI.updateRole(editingRole.id, formData);
      } else {
        response = await RolesAPI.createRole(formData);
      }

      if (response.success) {
        showSnackbar(editingRole ? t('role_updated_successfully') : t('role_created_successfully'));
        handleCloseModal();
        loadRoles();
        loadStatistics();
      } else {
        showSnackbar(response.error || (editingRole ? t('error_updating_role') : t('error_creating_role')), 'error');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showSnackbar(editingRole ? t('error_updating_role') : t('error_creating_role'), 'error');
    }
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteRole = async () => {
    try {
      const response = await RolesAPI.deleteRole(roleToDelete.id);

      if (response.success) {
        showSnackbar(t('role_deleted_successfully'));
        loadRoles();
        loadStatistics();
      } else {
        if (response.error === 'role_in_use_cannot_delete') {
          showSnackbar(t('role_in_use_cannot_delete'), 'error');
        } else {
          showSnackbar(response.error || t('error_deleting_role'), 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showSnackbar(t('error_deleting_role'), 'error');
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const renderStatistics = () => (
    <Grid container spacing={3} className={classes.headerSection}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <SecurityIcon style={{ fontSize: 40, color: colors.MAIN_COLOR, marginBottom: 8 }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: colors.MAIN_COLOR }}>
            {statistics.totalRoles || 0}
          </Typography>
          <Typography variant="body2">{t('total_roles')}</Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <PeopleIcon style={{ fontSize: 40, color: '#4CAF50', marginBottom: 8 }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
            {statistics.totalUsers || 0}
          </Typography>
          <Typography variant="body2">{t('total_users')}</Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <AssignmentIcon style={{ fontSize: 40, color: '#FF9800', marginBottom: 8 }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
            {statistics.usersWithCustomRoles || 0}
          </Typography>
          <Typography variant="body2">{t('users_with_custom_roles') || 'With Custom Roles'}</Typography>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <TodayIcon style={{ fontSize: 40, color: '#2196F3', marginBottom: 8 }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
            {statistics.activitiesToday || 0}
          </Typography>
          <Typography variant="body2">{t('activities_today')}</Typography>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRolesTable = () => (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeader}>{t('role_name') || 'ROL'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('role_description') || 'DESCRIPCIÓN'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('permissions') || 'PERMISOS'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('users') || 'USUARIOS'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('role_status') || 'ESTADO'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('role_created_date') || 'FECHA CREACIÓN'}</TableCell>
            <TableCell className={classes.tableHeader}>{t('role_actions') || 'ACCIONES'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((role) => (
            <TableRow key={role.id} className={classes.tableRow}>
              <TableCell>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                  {role.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" style={{ color: '#666' }}>
                  {role.description}
                </Typography>
              </TableCell>
              <TableCell className={classes.permissionsCell}>
                <Typography variant="body2">
                  {role.permissions?.length || 0} páginas
                </Typography>
                <Typography variant="caption" style={{ color: '#999' }}>
                  {role.permissions?.length > 0 ? '0 editar, 0 ver, 0 ambos' : '0 editar, 0 ver, 0 ambos'}
                </Typography>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PeopleIcon style={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2">
                    {role.userCount || 0}
                  </Typography>
                </div>
              </TableCell>
              <TableCell>
                <Switch
                  checked={role.isActive}
                  onChange={(e) => {
                    // Handle status change here if needed
                    console.log('Status change for role:', role.name, 'to:', e.target.checked);
                  }}
                  color="primary"
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(role.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell>
                <div className={classes.actionsCell}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenModal(role)}
                    style={{ color: colors.MAIN_COLOR }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRole(role)}
                    style={{ color: '#F44336' }}
                    disabled={role.userCount > 0}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderModal = () => (
    <Modal
      open={modalOpen}
      onClose={handleCloseModal}
      className={classes.modal}
    >
      <div className={classes.modalContent}>
        <div className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            {editingRole ? t('edit_role') : t('create_role')}
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </div>

        <TextField
          fullWidth
          label={t('role_name')}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label={t('role_description')}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          margin="normal"
          multiline
          rows={3}
          required
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
            />
          }
          label={t('active')}
          style={{ margin: '16px 0' }}
        />

        <Typography variant="subtitle1" style={{ marginTop: '20px', fontWeight: 'bold' }}>
          {t('page_permissions')}:
        </Typography>
        <Typography variant="body2" style={{ color: '#666', marginBottom: '16px' }}>
          {t('select_permissions')}
        </Typography>

        {/* Debug Info */}
        <Typography variant="caption" style={{ color: '#999', fontStyle: 'italic', marginBottom: '8px', display: 'block' }}>
          Debug: {Object.keys(availablePages).length} pages available, {formData.permissions.length} permissions selected
        </Typography>

        <FormGroup className={classes.permissionsGrid}>
          {Object.entries(availablePages).length === 0 ? (
            <Typography variant="body2" style={{ color: '#f44336', padding: '16px', textAlign: 'center' }}>
              ⚠️ No available pages loaded. Check API connection.
            </Typography>
          ) : (
            Object.entries(availablePages).map(([key, page]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={formData.permissions.includes(key)}
                    onChange={(e) => {
                      console.log(`🔘 Permission ${key} changed to:`, e.target.checked);
                      handlePermissionChange(key, e.target.checked);
                    }}
                  />
                }
                label={
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {page.name} ({key})
                    </Typography>
                    <Typography variant="caption" style={{ color: '#666' }}>
                      {page.description}
                    </Typography>
                  </div>
                }
              />
            ))
          )}
        </FormGroup>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', gap: '12px' }}>
          <MuiButton onClick={handleCloseModal}>
            {t('cancel')}
          </MuiButton>
          <Button color="primary" onClick={handleSaveRole}>
            {editingRole ? t('update_role') : t('create_role')}
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {t('roles_and_responsibilities')}
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            {t('manage_roles_users_activities')}
          </Typography>
        </div>
        <Button
          color="primary"
          onClick={() => handleOpenModal()}
          startIcon={<AddIcon />}
          className={classes.createButton}
        >
          {t('create_role')}
        </Button>
      </div>

      {renderStatistics()}

      <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
        Lista de Roles
      </Typography>

      {roles.length === 0 ? (
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <SecurityIcon style={{ fontSize: 60, color: '#ccc', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
            No roles created yet
          </Typography>
          <Typography variant="body2" style={{ color: '#999' }}>
            Create your first role to start managing user permissions
          </Typography>
        </Card>
      ) : (
        renderRolesTable()
      )}

      {renderModal()}

      <AlertDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t('confirm_delete_role')}
        message={`Are you sure you want to delete the role "${roleToDelete?.name}"?`}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        onConfirm={confirmDeleteRole}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
} 